import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { t } from "i18next";
import { RenderFormikInput } from "./../../../../Components/Common/Forms/FormikInputHelper";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Alert,
} from "reactstrap";
import { useLinksStore } from "../../../../stores/Assets/links";
import ReactSelect from "react-select";
import { reactSelectStyling } from "../../../../Components/Common/Forms/FormikInputHelper";

const AddSiteLink = ({ sites, showAddSiteLinkModal, toggleAddSiteLinkModal }) => {
  const { createSiteLink, isLoading } = useLinksStore((state) => state);
  const [selectedSites, setSelectedSites] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");

  const formik = useFormik({
    initialValues: {
      user_email: "",
    },
    validationSchema: Yup.object({
      user_email: Yup.string()
        .email(t("Invalid email"))
        .required(t("Required")),
    }),
    onSubmit: async (values) => {
      if (selectedSites.length === 0) {
        alert(t("Please select at least one site"));
        return;
      }

      const json = {
        email: values.user_email,
        site_ids: selectedSites.map((s) => s.value),
      };

      const result = await createSiteLink(json);
      if (result) {
        setSuccessMessage(
          `Successfully linked user to ${selectedSites.length} site(s)!`
        );
        setTimeout(() => {
          formik.resetForm();
          setSelectedSites([]);
          setSuccessMessage("");
          toggleAddSiteLinkModal();
        }, 1500);
      }
    },
  });

  // Convert sites to React Select options
  const siteOptions = sites.map((site) => ({
    value: site.site_id,
    label: `${site.site_id} - ${site.name}`,
  }));

  return (
    <Modal isOpen={showAddSiteLinkModal} toggle={toggleAddSiteLinkModal} size="md">
      <ModalHeader toggle={toggleAddSiteLinkModal}>
        <i className="ri-building-2-line me-2"></i>
        {t("Link User to Sites")}
      </ModalHeader>
      <ModalBody>
        {successMessage && (
          <Alert color="success" className="d-flex align-items-center gap-2">
            <i className="ri-checkbox-circle-line fs-5"></i>
            {successMessage}
          </Alert>
        )}

        <Alert color="info" className="d-flex align-items-start gap-2">
          <i className="ri-information-line fs-5 mt-1"></i>
          <div>
            <strong>{t("Site-Level Access")}</strong>
            <p className="mb-0 mt-1 small">
              {t("This will give the user access to the selected sites without linking them to specific spaces. Use this for site managers or administrators.")}
            </p>
          </div>
        </Alert>

        <form
          onSubmit={formik.handleSubmit}
          className="d-flex flex-column flex-wrap"
        >
          <div className="flex-fill mb-2">
            <div className="flex-fill mb-3 w-100">
              {RenderFormikInput(formik, {
                fieldName: "user_email",
                label: t("User Email"),
                fullWidth: true,
              })}
            </div>

            <Label for="sites" className="mb-1">
              {t("Sites")} <span className="text-danger">*</span>
              <small className="text-muted ms-2">(Select one or multiple)</small>
            </Label>
            <ReactSelect
              {...reactSelectStyling}
              id="sites"
              options={siteOptions}
              isMulti
              value={selectedSites}
              onChange={setSelectedSites}
              placeholder={t("Select Sites...")}
              className="mb-2"
            />
            {selectedSites.length > 0 && (
              <small className="text-success d-block mb-2">
                <i className="ri-checkbox-circle-line me-1"></i>
                {selectedSites.length} site(s) selected
              </small>
            )}
          </div>

          <div className="d-flex flex-row align-items-center justify-content-center gap-2 mt-2">
            <Button
              type="submit"
              color="success"
              disabled={isLoading || selectedSites.length === 0}
              className="d-flex align-items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" />
                  <span>{t("Linking...")}</span>
                </>
              ) : (
                <>
                  <i className="ri-building-2-line"></i>
                  <span>{t("Link to Sites")}</span>
                </>
              )}
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                formik.resetForm();
                setSelectedSites([]);
                setSuccessMessage("");
                toggleAddSiteLinkModal();
              }}
              disabled={isLoading}
            >
              {t("Cancel")}
            </Button>
          </div>
        </form>
      </ModalBody>
      <ModalFooter />
    </Modal>
  );
};

export default AddSiteLink;



